import { BleClient } from '@capacitor-community/bluetooth-le';

// Receipt printers (ESC/POS thermal)
const BLE_RECEIPT_SERVICES = [
  "0000ff00-0000-1000-8000-00805f9b34fb", // common ESC/POS
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // ISSC SPP
  "000018f0-0000-1000-8000-00805f9b34fb", // Bixolon, Star
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2", // Goojprt RP58
  "0000fee7-0000-1000-8000-00805f9b34fb", // generic
];

// Label printers (TSPL / ZPL / ESC-POS over BLE)
// Most BT label printers share the same SPP-over-GATT services as receipt
// printers; keep a separate constant so we can tighten the filter later.
const BLE_LABEL_SERVICES = [
  ...BLE_RECEIPT_SERVICES,
];

// Back-compat alias — used by existing characteristic discovery.
const BLE_PRINTER_SERVICES = BLE_RECEIPT_SERVICES;

// Helper to generate a unique storage key for each user AND printer type.
// Falls back to the legacy key for "receipt" so current users don't lose connection!
const getStorageKey = (userId: string, printerType: "receipt" | "label" = "receipt") => {
  return printerType === "receipt"
    ? `cached_bt_printer_${userId}`
    : `cached_bt_printer_${printerType}_${userId}`;
};

// Helper to find the writable characteristic natively
async function discoverWriteCharacteristic(deviceId: string) {
  const services = await BleClient.getServices(deviceId);

  for (const service of services) {
    if (BLE_PRINTER_SERVICES.includes(service.uuid.toLowerCase())) {
      for (const characteristic of service.characteristics) {
        if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
          return {
            serviceUuid: service.uuid,
            characteristicUuid: characteristic.uuid
          };
        }
      }
    }
  }
  return null;
}

export async function autoConnectBTPrinter(userId: string): Promise<boolean> {
  return false;
}

// Pairs a Bluetooth printer using a pre-selected device id (from our custom
// scan dialog) — bypasses OS native picker entirely so we control UX.
//   - device.deviceId + name come from `useBleScan` results
//   - characteristic discovery + cache write unchanged
export async function connectBTPrinterById(
  userId: string,
  printerType: "receipt" | "label",
  device: { deviceId: string; name: string }
): Promise<string> {
  if (!userId) throw new Error("User ID is required to save a printer.");

  try {
    await BleClient.initialize({ androidNeverForLocation: true });

    const isEnabled = await BleClient.isEnabled();
    if (!isEnabled) {
      try {
        await BleClient.requestEnable();
      } catch (err) {
        throw new Error("Bluetooth is off. Please turn it on to connect.");
      }
    }

    await BleClient.connect(device.deviceId);

    const charData = await discoverWriteCharacteristic(device.deviceId);
    if (!charData) {
      await BleClient.disconnect(device.deviceId);
      throw new Error(
        "Connected, but this device doesn't expose a printer-compatible channel. Pick a different device."
      );
    }

    const printerData = {
      id: device.deviceId,
      name: device.name || "Thermal Printer",
      serviceUuid: charData.serviceUuid,
      characteristicUuid: charData.characteristicUuid,
    };

    localStorage.setItem(getStorageKey(userId, printerType), JSON.stringify(printerData));
    return printerData.name;
  } catch (error: any) {
    throw new Error(error.message || "Failed to pair Bluetooth printer.");
  }
}

/** Service UUIDs to filter for in the custom scan dialog. */
export function getPrinterServiceFilter(printerType: "receipt" | "label"): string[] {
  return printerType === "label" ? BLE_LABEL_SERVICES : BLE_RECEIPT_SERVICES;
}

// Legacy entry point — kept as a thin wrapper that uses requestDevice. Now
// only called by code paths we haven't migrated yet (PrinterSettings wizard).
// Prefer `connectBTPrinterById` from the BTScanDialog flow.
export async function connectBTPrinter(
  userId: string,
  printerType: "receipt" | "label" = "receipt",
  opts: { allowAll?: boolean } = {}
): Promise<string> {
  if (!userId) throw new Error("User ID is required to save a printer.");

  try {
    await BleClient.initialize({ androidNeverForLocation: true });
    const isEnabled = await BleClient.isEnabled();
    if (!isEnabled) {
      try {
        await BleClient.requestEnable();
      } catch (err) {
        throw new Error("Bluetooth is off. Please turn it on to connect.");
      }
    }

    const requestOpts: any = { allowDuplicates: false };
    if (!opts.allowAll) {
      requestOpts.services = getPrinterServiceFilter(printerType);
    }

    let device;
    try {
      device = await BleClient.requestDevice(requestOpts);
    } catch (err: any) {
      const msg = err?.message || String(err);
      if (/cancel/i.test(msg)) throw new Error("Pairing cancelled.");
      throw new Error(
        `No ${printerType} printers found. Make sure the printer is on, in pairing mode, and within range.`
      );
    }

    return connectBTPrinterById(userId, printerType, {
      deviceId: device.deviceId,
      name: device.name || "Thermal Printer",
    });
  } catch (error: any) {
    throw new Error(error.message || "Failed to pair Bluetooth printer.");
  }
}

// UPDATED: Added printerType argument
export async function sendToBTPrinter(userId: string, data: Uint8Array, printerType: "receipt" | "label" = "receipt"): Promise<void> {
  if (!userId) throw new Error("User ID is required to print.");

  const cachedStr = localStorage.getItem(getStorageKey(userId, printerType));
  if (!cachedStr) {
    throw new Error(`No ${printerType} BT printer connected for your account. Please pair a printer first.`);
  }

  const printerData = JSON.parse(cachedStr);

  await BleClient.initialize({ androidNeverForLocation: true });

  const isEnabled = await BleClient.isEnabled();
  if (!isEnabled) {
    try {
      await BleClient.requestEnable();
    } catch (err) {
      throw new Error("Bluetooth is off. Please turn it on to print.");
    }
  }

  try {
    await BleClient.connect(printerData.id);
  } catch (e: any) {
    if (e.message && !e.message.toLowerCase().includes("already connected")) {
      throw new Error(`Could not connect to ${printerData.name}. Make sure it is powered on.`);
    }
  }

  const dataView = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const chunkSize = 100;

  for (let i = 0; i < dataView.byteLength; i += chunkSize) {
    const end = Math.min(i + chunkSize, dataView.byteLength);
    const chunk = new DataView(dataView.buffer, dataView.byteOffset + i, end - i);

    await BleClient.writeWithoutResponse(
      printerData.id,
      printerData.serviceUuid,
      printerData.characteristicUuid,
      chunk
    );

    await new Promise((r) => setTimeout(r, 40));
  }
}

// UPDATED: Added printerType argument
export function getCachedBTPrinter(userId: string, printerType: "receipt" | "label" = "receipt"): { id: string, name: string } | null {
  if (!userId) return null;

  const cachedStr = localStorage.getItem(getStorageKey(userId, printerType));
  if (cachedStr) {
    const parsed = JSON.parse(cachedStr);
    return { id: parsed.id, name: parsed.name };
  }
  return null;
}

// UPDATED: Added printerType argument
export async function clearBTPrinterCache(userId: string, printerType: "receipt" | "label" = "receipt"): Promise<void> {
  if (!userId) return;

  const cachedStr = localStorage.getItem(getStorageKey(userId, printerType));
  if (cachedStr) {
    try {
      const parsed = JSON.parse(cachedStr);
      await BleClient.initialize({ androidNeverForLocation: true });
      await BleClient.disconnect(parsed.id);
    } catch (_) { }

    localStorage.removeItem(getStorageKey(userId, printerType));
  }
}